import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Download,
  Zap,
  CheckCircle2,
  AlertCircle,
  Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ArtifactProgressCard
 * Mostra o progresso de processamento de um artefato específico
 * Exibido ao lado do card principal do artefato durante carregamento
 */
const ArtifactProgressCard = ({ 
  segmentId, 
  token, 
  isYouTubeUrl,
  artifactType,
  isGenerating
}) => {
  const [status, setStatus] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);

  // Iniciar polling quando começar a gerar
  useEffect(() => {
    if (isYouTubeUrl && segmentId && token && isGenerating) {
      setStartTime(Date.now());
      checkStatus();
      setPollingActive(true);
    } else {
      setPollingActive(false);
      setElapsedTime(0);
    }
  }, [segmentId, isYouTubeUrl, token, isGenerating]);

  // Polling para atualizar status
  useEffect(() => {
    if (!pollingActive || !segmentId || !token) return;

    const pollInterval = setInterval(() => {
      checkStatus();
    }, 1000); // Atualizar a cada 1 segundo para mais responsividade

    return () => clearInterval(pollInterval);
  }, [pollingActive, segmentId, token]);

  // Atualizar tempo decorrido
  useEffect(() => {
    if (!isGenerating || !startTime) return;

    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [isGenerating, startTime]);

  const checkStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/youtube/${segmentId}/status`,
        { headers }
      );

      setStatus(response.data);

      // Parar polling quando concluído ou erro
      if (response.data.status === 'COMPLETED' || response.data.status === 'ERROR') {
        setPollingActive(false);
      }
    } catch (error) {
      // Erros esperados são ignorados durante polling
      console.debug('Status check:', error.message);
    }
  };

  if (!isYouTubeUrl || !isGenerating || !status) {
    return null;
  }

  const getProgressPercent = () => {
    if (!status.progress) return 0;
    return status.progress.percent || 0;
  };

  const getStatusMessage = () => {
    switch (status.status) {
      case 'DOWNLOADING':
        return 'Baixando vídeo do YouTube...';
      case 'TRANSCRIBING':
        return 'Transcrevendo áudio...';
      case 'PROCESSING':
        return 'Gerando artefato com IA...';
      case 'PROCESSED':
        return 'Processamento concluído!';
      case 'COMPLETED':
        return 'Artefato gerado com sucesso!';
      case 'ERROR':
        return 'Erro no processamento';
      default:
        return 'Processando...';
    }
  };

  const getStatusIcon = () => {
    switch (status.status) {
      case 'DOWNLOADING':
        return <Download className="w-5 h-5 text-blue-500 animate-bounce" />;
      case 'TRANSCRIBING':
        return <Zap className="w-5 h-5 text-purple-500 animate-pulse" />;
      case 'PROCESSING':
        return <Zap className="w-5 h-5 text-orange-500 animate-pulse" />;
      case 'COMPLETED':
      case 'PROCESSED':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'ERROR':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'DOWNLOADING':
        return 'border-blue-200 bg-blue-50';
      case 'TRANSCRIBING':
      case 'PROCESSING':
        return 'border-purple-200 bg-purple-50';
      case 'COMPLETED':
      case 'PROCESSED':
        return 'border-green-200 bg-green-50';
      case 'ERROR':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const showLongVideoWarning = elapsedTime > 30 && status.status !== 'COMPLETED' && status.status !== 'PROCESSED';

  return (
    <Card className={`border-2 ${getStatusColor()} mb-4`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {getStatusIcon()}
            <span>{getStatusMessage()}</span>
          </CardTitle>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {elapsedTime}s
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Progresso:</span>
            <span>{getProgressPercent()}%</span>
          </div>
          <Progress value={getProgressPercent()} className="h-2" />
        </div>

        {/* Status Details */}
        {status.status && (
          <div className="text-sm text-gray-600 space-y-1">
            {status.progress?.step && (
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-blue-400 rounded-full"></span>
                <span>{status.progress.step}</span>
              </div>
            )}
            
            {/* Timestamps */}
            {status.downloaded_at && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Baixado: {new Date(status.downloaded_at).toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
            {status.transcribed_at && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Transcrito: {new Date(status.transcribed_at).toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
          </div>
        )}

        {/* Long Video Warning */}
        {showLongVideoWarning && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
            <p className="font-medium">⏱️ Vídeo muito longo</p>
            <p className="text-xs mt-1">
              Este vídeo está levando mais tempo do que o normal para processar. 
              Isso é esperado para vídeos longos. Favor aguardar...
            </p>
          </div>
        )}

        {/* Error Message */}
        {status.status === 'ERROR' && status.error_message && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            <p className="font-medium">❌ Erro</p>
            <p className="text-xs mt-1">{status.error_message}</p>
          </div>
        )}

        {/* Captions Available */}
        {status.captions && (status.status === 'PROCESSED' || status.status === 'COMPLETED') && (
          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
            <p className="font-medium text-green-700">✓ Legendas disponíveis</p>
            <div className="flex gap-2 mt-2 flex-wrap">
              {status.captions.vtt && <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">VTT</span>}
              {status.captions.srt && <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">SRT</span>}
              {status.captions.json && <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">JSON</span>}
              {status.captions.txt && <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">TXT</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ArtifactProgressCard;
